import DataSourceWidgetView from '../data-source-widget-view'
import LinearGaugeConfig from './linear-gauge-config'
import _ from 'lodash'
import Linear from 'scoreboard/components/gauge/linear'
import template from './linear-gauge-view.hbs'
import datasetWidgetDrilldown from '../dataset-widget-drilldown'

var LinearGaugeView = DataSourceWidgetView.extend({
	className: 'widget linear-gauge has-drilldown',
	resizable: true,
	configClass: LinearGaugeConfig,
	template: template,

	nonFetchProperties: DataSourceWidgetView.prototype.nonFetchProperties.concat(
		'showScore', 'showThresholds', 'showOrganization', 'showPointerLabel',
		'showPeriod', 'gaugeHeight'),

	initialize: function () {
		DataSourceWidgetView.prototype.initialize.apply(this, arguments)
		_.bindAll(this, 'resize', 'endResize')

		// #30713 normally this minHeight method would be defined in the model
		// However, we need to use the total height of the SVG in the DOM as
		// part of the calculations, which we don't have access to in the model.
		// So we'll manually add the function here so we can use the DOM node
		this.model.containerDefaults.minHeight = () => {
			let totalHeight = parseFloat(this.gaugeSvg().attr('height'))
			let gaugeHeight = parseInt(this.model.properties.get('gaugeHeight'))

			let minGaugeHeight = 10
			if (this.model.properties.get('showThresholds')) {
				minGaugeHeight += 15
			}
			if (this.model.properties.get('showPointerLabel')) {
				minGaugeHeight += 25
			}

			// If we subtract the height of the gauge itself from the total height,
			// that leaves us with the space needed for the labels
			let labelHeight = totalHeight - gaugeHeight

			// We don't want the wob to be smaller than a minimum height for the
			// gauge itself, plus the space used for the labels
			return labelHeight + minGaugeHeight
		}
	},

	render: function () {
		// We don't want to listen to resized events while we're rendering,
		// because the height may increase and we'll be manually triggering
		// resize; we don't want to get in an infinite loop
		this._toggleResizedListener(false)

		DataSourceWidgetView.prototype.render.apply(this, arguments)
	},

	/**
	 * @override
	 */
	renderContent ({ datasetQueryResponse } = {}) {
		this._draw({ datasetQueryResponse })

		// Now that all rendering is done, this will let the wob know that
		// the dimensions may need to change
		this._wobResize(false)

		// And now we can listen for standard resized events again
		this._toggleResizedListener(true)

		this.readyForDisplayAndExport()

		return this
	},

	_toggleResizedListener: function (on) {
		if (on) {
			this.container
				// just to make sure we don't double-bind the listeners
				.off('resized', this.resize)
				.off('end:resize', this.endResize)
				// 'resized' is raised on each px change as the user is dragging
				.on('resized', this.resize)
				// 'end:resize' is raised when the user is done dragging (ie mouseup)
				.on('end:resize', this.endResize)
		} else {
			this.container
				.off('resized', this.resize)
				.off('end:resize', this.endResize)
		}
	},

	_draw: function ({ datasetQueryResponse } = {}) {
		const data = this.model.relevantData()
		const opt = this.model.gaugeOptions({ datasetQueryResponse })

		if (this.linear) {
			this.linear.options = opt

			this.linear.render()
		} else {
			this.linear = new Linear(opt)
			this.$('.gauge-wrapper').prepend(this.linear.el)

			this.linear.render()
		}

		this.$el.toggleClass('is-not-permitted', !data.permitted)

		this.toggleEmptyState(data.error)
	},

	resize: function (e, dimensions) {
		if (dimensions && dimensions.previous) {
			var props = this.model.properties.toJSON()

			// Since the aspect ratio isn't locked, and the height could vary depending on what options are selected
			// and how long the description is, we need to adjust the gauge image's height only by the amount
			// that the wob's height was increased

			let newGaugeHeight = parseFloat(props.gaugeHeight) + dimensions.height - dimensions.previous.height

			// Determining old and current orientation using the wob dimensions.
			// An alternative would be to inspect the SVG for the horizontal/vertical
			// CSS classes, but at this point, the new dimensions haven't been
			// applied yet, so the CSS classes can only tell use what the orientation
			// *was*, not what it will be after resizing
			let oldOrientation = orientation(dimensions.previous)
			let newOrientation = orientation(dimensions)

			// #30713 when a resize causes the gauge to flip orientations, the
			// gauge height needs to be adjusted to keep the wob the same size.
			if (oldOrientation !== newOrientation) {
				let multilineLabel = this.gaugeSvg().find('.title text').length > 1

				// I honestly don't know where the magic number 17 comes from, but
				// it seems to work for single-line labels. 1 works for multi-line
				// labels, though again, I don't know why
				let adjustment = multilineLabel ? 1 : 17

				if (oldOrientation === 'horizontal' && newOrientation === 'vertical') {
					newGaugeHeight -= adjustment
				} else if (oldOrientation === 'vertical' && newOrientation === 'horizontal') {
					newGaugeHeight += adjustment
				}
			}

			this.model.properties.set({
				// Only increase the gauge height by the amount the size increased
				'gaugeHeight': Math.max(0, newGaugeHeight)
				// We don't care about the width, because it will always be the full width of the wob
			})

			this._draw({ datasetQueryResponse: this.lastDatasetQueryResponse })

			// Redrawing the SVG may change its dimensions, particularly if it
			// changed how the label wraps. So adjust the wob dimensions to match
			this._toggleResizedListener(false)
			this._wobResize(false)
			this._toggleResizedListener(true)
		}
	},

	endResize: function () {
		this._resizing = false // This was set on start:resize, be sure it's cleaned up in case our event handler fires before one that closes it.
		this._toggleResizedListener(false)

		// #13828 once user is done resizing the wob (ie on mouseup), it's possible that text wrapping/unwrapping
		// has made the wob content smaller or larger that the wob itself. So resize the wob to fit the content
		this._wobResize(true)

		this._toggleResizedListener(true)
	},

	_wobResize: function (animate) {
		if (this._resizing) {
			// canvas code or something is resizing this wob, don't do anything
			return
		}
		this.model.wob.resize(null, parseFloat(this.gaugeSvg().attr('height')), undefined, undefined, { animate: animate })
	},

	allowConfig: function () {
		// even if this is a template copy, we can still change the drilldown
		return true
	},

	gaugeSvg: function () {
		return this.$('svg.linear-gauge')
	},

	/**
	 * @override
	 */
	drilldown: datasetWidgetDrilldown,

	remove: function () {
		DataSourceWidgetView.prototype.remove.apply(this, arguments)
		this.linear && this.linear.remove()
	}
})

function orientation (dimensions) {
	// Note: equal height/width is vertical
	return dimensions.height >= dimensions.width ? 'vertical' : 'horizontal'
}

export default LinearGaugeView

LinearGaugeView.__filename__ = 'dashboards/widgets/linear-gauge/linear-gauge-view.js'
